import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { FaCheck } from 'react-icons/fa';
import { shipmentService } from '../../../services/shipmentService';
import { activityTracker } from '../../../services/activityTracker';
import { recordSenderUse } from '../../../utils/senderUsage';
import { getActiveIFSAccountId } from '../../../utils/ifsAccount';
import { WizardContainer, WizardStepIndicator, WizardStep, WizardContent, WizardActions, ActionButton, } from './IFSShippingDashboard.styles';
import SelectSenderStep from './WizardSteps/SelectSenderStep';
import SelectRecipientStep from './WizardSteps/SelectRecipientStep';
import PackageDetailsStep from './WizardSteps/PackageDetailsStep';
import CostPreviewStep from './WizardSteps/CostPreviewStep';
const STEPS = [
    { num: 1, label: 'Sender' },
    { num: 2, label: 'Recipient' },
    { num: 3, label: 'Package' },
    { num: 4, label: 'Review & Save' },
];
const CreateShipmentTab = ({ isMockMode }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [sender, setSender] = useState(null);
    const [recipient, setRecipient] = useState(null);
    const [pkg, setPkg] = useState(null);
    const [cost, setCost] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const canProceed = () => {
        switch (currentStep) {
            case 1: return sender !== null;
            case 2: return recipient !== null;
            case 3: return pkg !== null;
            case 4: return true;
            default: return false;
        }
    };
    const handleSave = async () => {
        if (!sender || !recipient || !pkg)
            return;
        setSaving(true);
        try {
            const res = await shipmentService.create({
                status: 'draft',
                source: 'wizard',
                sender,
                recipient,
                package: pkg,
                cost: cost || { finalAmount: '', breakdown: [], zoneId: '', calculatedAt: new Date().toISOString() },
                delivery: { deliveredTo: null, deliveredDate: null, deliveredSignature: null, fedexStatus: null },
                documents: { labelLink: null, returnLabelLink: null, commercialInvoiceLink: null, receiptLink: null },
            });
            if (res.ok) {
                setSaved(true);
                recordSenderUse(getActiveIFSAccountId(), sender.addressId || '');
                activityTracker.trackAction('shipment-draft-create', 'Created draft shipment', {
                    recipientName: recipient.name,
                    service: pkg.serviceType,
                });
            }
        }
        catch (err) {
            console.error('Failed to save draft:', err);
        }
        finally {
            setSaving(false);
        }
    };
    const handleReset = () => {
        setCurrentStep(1);
        setSender(null);
        setRecipient(null);
        setPkg(null);
        setCost(null);
        setSaved(false);
    };
    if (saved) {
        return (_jsx(WizardContainer, { children: _jsxs(WizardContent, { style: { textAlign: 'center', padding: '3rem' }, children: [_jsx("div", { style: { fontSize: '3rem', color: '#10b981', marginBottom: '1rem' }, children: _jsx(FaCheck, {}) }), _jsx("h2", { style: { color: 'var(--text-primary)', marginBottom: '0.5rem' }, children: "Draft Saved!" }), _jsx("p", { style: { color: 'var(--text-secondary)', marginBottom: '1.5rem' }, children: "Your shipment draft has been saved. You can find it in the Shipments tab." }), _jsx(ActionButton, { "$variant": "primary", onClick: handleReset, children: "Create Another" })] }) }));
    }
    return (_jsxs(WizardContainer, { children: [_jsx(WizardStepIndicator, { children: STEPS.map(step => (_jsxs(WizardStep, { "$active": currentStep === step.num, "$completed": currentStep > step.num, children: [_jsx("div", { className: "step-circle", children: currentStep > step.num ? _jsx(FaCheck, { size: 14 }) : step.num }), _jsx("span", { className: "step-label", children: step.label })] }, step.num))) }), _jsxs(WizardContent, { children: [currentStep === 1 && _jsx(SelectSenderStep, { isMockMode: isMockMode, sender: sender, onSelect: setSender, onPicked: () => setCurrentStep(2) }), currentStep === 2 && _jsx(SelectRecipientStep, { isMockMode: isMockMode, recipient: recipient, onSelect: setRecipient, onPicked: () => setCurrentStep(3) }), currentStep === 3 && _jsx(PackageDetailsStep, { isMockMode: isMockMode, pkg: pkg, onChange: setPkg }), currentStep === 4 && _jsx(CostPreviewStep, { isMockMode: isMockMode, sender: sender, recipient: recipient, pkg: pkg, cost: cost, onCostCalculated: setCost })] }), _jsxs(WizardActions, { children: [_jsx(ActionButton, { onClick: () => setCurrentStep(s => s - 1), disabled: currentStep === 1, children: "Back" }), _jsx("div", { style: { display: 'flex', gap: '0.75rem' }, children: currentStep < 4 ? (_jsx(ActionButton, { "$variant": "primary", onClick: () => setCurrentStep(s => s + 1), disabled: !canProceed(), children: "Next" })) : (_jsx(ActionButton, { "$variant": "primary", onClick: handleSave, disabled: saving, children: saving ? 'Saving...' : 'Save as Draft' })) })] })] }));
};
export default CreateShipmentTab;
